import type { OnlineDevice } from '@/@types/device';
import type { StopTimeData } from '@/@types/gtfs-ressource';

/**
 * Only a bus running the very trip of the stop time can have served it: a device that reached the
 * stop time through its block runs another trip, whose sequence says nothing here.
 * The service day is not compared again: `device_id` only comes from the lib attaching the bus to this
 * occurrence, with the start_date it corrects after midnight.
 */
function hasDevicePassedStopTime(stopTime: StopTimeData, device: OnlineDevice | undefined): boolean {
  if (!device || device.trip_id !== stopTime.trip_id) return false;
  return device.current_stop_sequence !== undefined && device.current_stop_sequence > stopTime.stop_sequence;
}

const isServedBy = (devices: ReadonlyArray<OnlineDevice>) => {
  const deviceById = new Map(devices.map(device => [device.device_id, device]));
  return (st: StopTimeData) =>
    hasDevicePassedStopTime(st, st.device_id ? deviceById.get(st.device_id) : undefined);
};

/**
 * The stop times of the day with the served ones counted as departed long ago: the end-of-service
 * message then shows instead of an empty list, and a served bus is not taken for a departure to come.
 */
export function withServedStopTimesDeparted(
  stopTimes: ReadonlyArray<StopTimeData>,
  devices: ReadonlyArray<OnlineDevice>,
): StopTimeData[] {
  const isServed = isServedBy(devices);
  return stopTimes.map(st =>
    isServed(st) ? { ...st, departure_ts: 0, estimated_departure_ts: undefined } : st,
  );
}

/**
 * Stop times of `stopIds` still to come: upcoming by timetable, or carried by a bus (whose estimate
 * may run later than the timetable), unless that bus has already served them.
 */
export function stopTimesOfStops(
  stopTimes: ReadonlyArray<StopTimeData>,
  stopIds: ReadonlyArray<string>,
  nowTs: number,
  devices: ReadonlyArray<OnlineDevice>,
): StopTimeData[] {
  const isServed = isServedBy(devices);
  return stopIds.flatMap(stopId =>
    stopTimes.filter(
      st => st.stop_id === stopId && (st.departure_ts > nowTs || st.device_id) && !isServed(st),
    ),
  );
}
